import React, { useState, useEffect, useRef } from 'react';
import { X, MessageSquare, Send, Loader2, Heart, Trash2 } from 'lucide-react';
import { isSupabaseConfigured } from '../../config/supabase';

function ProjectCommentsModal({ isOpen, onClose, project, liveData, onAddComment, onLikeComment, onAddReply, isAdmin, onDeleteComment }) {
    const [newComment, setNewComment] = useState({ name: '', mobile: '', text: '' });
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const commentsEndRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('menu-open');
            if (commentsEndRef.current) commentsEndRef.current.scrollIntoView({ behavior: "smooth" });
        } else {
            document.body.classList.remove('menu-open');
        }
        return () => document.body.classList.remove('menu-open');
    }, [isOpen, liveData]);

    if (!isOpen || !project) return null;

    const comments = liveData[project.id]?.comments || [];
    const isOnline = navigator.onLine && isSupabaseConfigured;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newComment.name && newComment.text) {
            setIsSubmitting(true);
            await onAddComment(project.id, newComment);
            setNewComment({ name: '', mobile: '', text: '' });
            setIsSubmitting(false);
        }
    };

    const handleReply = async (commentIdx) => {
        if (replyText.trim()) {
            setIsSubmitting(true);
            await onAddReply(project.id, commentIdx, replyText);
            setReplyText("");
            setReplyingTo(null);
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm modal-overlay" onClick={onClose}></div>
            
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative z-10 overflow-hidden modal-content flex flex-col h-[80vh]">
                <div className="bg-slate-900 p-4 flex justify-between items-center text-white shrink-0">
                    <div>
                        <h3 className="text-base font-bold line-clamp-1">{project.title}</h3>
                        <p className="text-slate-400 text-xs flex items-center gap-1">
                            {isOnline ? (
                                <>
                                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                    Live Community Feedback
                                </>
                            ) : (
                                <>
                                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                    Local Comments (Offline)
                                </>
                            )}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                        <X size={18} />
                    </button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 space-y-6 bg-slate-50">
                    {comments.length === 0 ? (
                        <div className="text-center py-10 text-slate-400">
                            <MessageSquare className="mx-auto mb-2 opacity-50" size={32} />
                            <p className="text-sm">No comments yet. Be the first!</p>
                        </div>
                    ) : (
                        comments.map((c, idx) => (
                            <div key={idx} className="animate-fade-in group">
                                <div className="flex gap-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold shrink-0 text-xs border border-blue-200">
                                        {c.name.charAt(0).toUpperCase()}
                                    </div>
                                    
                                    <div className="flex-1">
                                        <div className="bg-white p-3 rounded-2xl rounded-tl-none shadow-sm border border-slate-100 relative">
                                            {isAdmin && (
                                                <button 
                                                    onClick={() => {
                                                        if (window.confirm("Permanently delete this comment?")) {
                                                            onDeleteComment(project.id, idx);
                                                        }
                                                    }}
                                                    className="absolute top-2 right-2 text-red-400 hover:text-red-600 p-1"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                            
                                            <div className="flex justify-between items-start mb-1">
                                                <span className="font-bold text-xs text-slate-800">{c.name}</span>
                                                <span className="text-[10px] text-slate-400 pr-4">{c.date}</span>
                                            </div>
                                            <p className="text-sm text-slate-600">{c.text}</p>
                                        </div>
                                        
                                        <div className="flex items-center gap-4 mt-2 px-1">
                                            <button 
                                                onClick={() => onLikeComment(project.id, idx)} 
                                                className="flex items-center gap-1 text-[11px] font-bold text-slate-400 hover:text-red-500 transition-colors"
                                            >
                                                <Heart size={12} className={c.likes > 0 ? "fill-red-500 text-red-500" : ""} /> 
                                                {c.likes || 0}
                                            </button>
                                            
                                            <button 
                                                onClick={() => setReplyingTo(replyingTo === idx ? null : idx)} 
                                                className="text-[11px] font-bold text-slate-400 hover:text-blue-600 transition-colors"
                                            >
                                                Reply
                                            </button>
                                        </div>

                                        {c.replies && c.replies.length > 0 && (
                                            <div className="mt-3 space-y-3 pl-4 border-l-2 border-slate-200">
                                                {c.replies.map((reply, ridx) => (
                                                    <div key={ridx} className="flex gap-2">
                                                        <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-[10px]">
                                                            {reply.name.charAt(0)}
                                                        </div>
                                                        <div className="bg-slate-100/50 p-2 rounded-xl flex-1 text-xs">
                                                            <div className="font-bold text-slate-700 mb-0.5">{reply.name}</div>
                                                            <p className="text-slate-600">{reply.text}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {replyingTo === idx && (
                                            <div className="mt-3 flex gap-2">
                                                <input 
                                                    autoFocus 
                                                    value={replyText} 
                                                    onChange={e => setReplyText(e.target.value)} 
                                                    className="flex-1 px-3 py-1.5 bg-white border border-blue-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-blue-500" 
                                                    placeholder="Write a reply..." 
                                                />
                                                <button 
                                                    onClick={() => handleReply(idx)} 
                                                    disabled={isSubmitting} 
                                                    className="p-1.5 bg-blue-600 text-white rounded-lg"
                                                >
                                                    <Send size={14}/>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                    <div ref={commentsEndRef} />
                </div>
                
                <div className="p-4 bg-white border-t border-slate-200 shrink-0">
                    <form onSubmit={handleSubmit} className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                            <input 
                                type="text" 
                                placeholder="Your Name" 
                                required 
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 outline-none" 
                                value={newComment.name} 
                                onChange={e => setNewComment({...newComment, name: e.target.value})} 
                            />
                            <input 
                                type="tel" 
                                placeholder="Mobile (Optional)" 
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 outline-none" 
                                value={newComment.mobile} 
                                onChange={e => setNewComment({...newComment, mobile: e.target.value})} 
                            />
                        </div>
                        
                        <div className="relative">
                            <input 
                                type="text" 
                                placeholder="Write a comment..." 
                                required 
                                className="w-full pl-3 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 outline-none" 
                                value={newComment.text} 
                                onChange={e => setNewComment({...newComment, text: e.target.value})} 
                            />
                            <button 
                                type="submit" 
                                disabled={isSubmitting} 
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {isSubmitting ? <Loader2 size={14} className="animate-spin"/> : <Send size={14} />}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ProjectCommentsModal;